import type {
  BorderProps,
  TableCell,
  TableOptions,
  TableRow,
} from '@features/powerpoint';
import { themeV2 } from '../domain/theme-v2';
import { DEFAULT_FONT } from './constants-v2';
import { textStylesV2 } from './text-styles-v2';

const NO_BORDER: BorderProps = { type: 'none' };

const BLUE_BORDER: BorderProps = {
  type: 'solid',
  color: themeV2.colors.magnetBlue,
  pt: 1,
};

const generateTableCell = (
  cell: TableCell,
  showBottomBorder: boolean
): TableCell => {
  return {
    text: cell.text,
    options: {
      valign: 'middle',
      fontFace: DEFAULT_FONT,
      fill: { type: 'none' },
      border: [
        NO_BORDER,
        NO_BORDER,
        showBottomBorder ? BLUE_BORDER : NO_BORDER,
        NO_BORDER,
      ],
      ...cell.options,
    },
  };
};

const generateRow = (cells: TableCell[], showBottomBorder = true): TableRow => {
  return cells.map(cell => {
    return generateTableCell(cell, showBottomBorder);
  });
};

export const generateTableV2 = (
  columnLabels: TableCell[],
  rowTextValues: TableCell[][],
  options?: TableOptions
) => {
  const rows = rowTextValues.map((data, index) => {
    const isLastRow = index === rowTextValues.length - 1;
    return generateRow(data, !isLastRow);
  });

  const tableConfigurations = {
    tableRows: [generateRow(columnLabels), ...rows],
    options: {
      w: 11.81,
      h: 1.22,
      x: 0.76,
      y: 1.64,
      rowH: 0.5,
      fontSize: 8,
      ...textStylesV2.contentSlideDefaults,
      ...options,
    },
  };
  return tableConfigurations;
};

type BuildStyledTableV2Options = {
  columns: string[];
  rows: string[][];
  options?: Parameters<typeof generateTableV2>[2];
};

export const buildStyledTableV2 = (options: BuildStyledTableV2Options) => {
  return generateTableV2(
    options.columns.map(
      (column): Parameters<typeof generateTableV2>[0][number] => ({
        text: column,
        options: {
          color: themeV2.colors.metricBlue,
          bold: true,
          fontFace: DEFAULT_FONT,
        },
      })
    ),
    options.rows.map((row): Parameters<typeof generateTableV2>[1][number] =>
      row.map(cell => ({ text: cell }))
    ),
    options.options
  );
};
